import { Pipe, PipeTransform, inject } from '@angular/core';

import { ReportI18nService } from './report-i18n.service';

/** Formats reconnus par {@link ReportFmtPipe}. */
export type ReportFormat = 'num' | 'pct' | 'mark' | 'date' | 'datetime';

/**
 * Libellé du rapport, clé relative à `REPORT.` — `{{ 'summary.score' | reportT }}`.
 *
 * Les variables suivent la convention du service : c'est la forme de l'objet
 * qui dit comment formater (`{ score: { mark: 9 } }`, `{ count: 3 }` pour
 * déclencher le pluriel).
 *
 * **Pipe impur assumé.** Le lecteur bascule la langue sans quitter la page — le
 * sélecteur est dans l'en-tête du rapport — et le libellé rendu doit alors
 * changer. Un pipe pur ne serait réévalué que si la *clé* changeait, or elle ne
 * change jamais : le texte resterait figé dans la langue précédente. On paie
 * donc une réévaluation à chaque cycle de détection ; c'est un coût délibéré,
 * pas un oubli. Il reste modeste : la transformation est une lecture d'objet
 * suivie d'un `replace`, sans allocation notable.
 */
@Pipe({
  name: 'reportT',
  standalone: true,
  pure: false,
})
export class ReportTPipe implements PipeTransform {
  private readonly i18n = inject(ReportI18nService);

  transform(key: string, vars?: Record<string, unknown>): string {
    return this.i18n.t(key, vars);
  }
}

/**
 * Valeur chiffrée ou datée du rapport — `{{ 43 | reportFmt: 'pct' }}`.
 *
 * Un seul pipe pour les cinq formats : c'est le service qui tient la locale
 * (chiffres latins en arabe, mois maghrébins…), et le rapport doit afficher
 * partout la même forme de nombre. Cinq pipes séparés auraient invité à
 * formater à la main ici ou là.
 *
 * **Pipe impur assumé**, pour la même raison que {@link ReportTPipe} : la
 * langue peut changer alors que la page est ouverte, et « 43 % » n'a pas la
 * même écriture d'une langue à l'autre. Un pipe pur ne verrait pas la bascule,
 * la valeur d'entrée étant inchangée.
 */
@Pipe({
  name: 'reportFmt',
  standalone: true,
  pure: false,
})
export class ReportFmtPipe implements PipeTransform {
  private readonly i18n = inject(ReportI18nService);

  transform(value: number | string, fmt: ReportFormat, digits?: number): string {
    switch (fmt) {
      case 'date':
        return this.i18n.date(String(value));
      case 'datetime':
        return this.i18n.dateTime(String(value));
      case 'pct':
        return this.i18n.pct(Number(value), digits);
      case 'mark':
        return this.i18n.mark(Number(value));
      default:
        return this.i18n.num(Number(value), digits);
    }
  }
}
